import React, { useEffect, useState } from "react";

// ── Marquee strip ─────────────────────────────────────────────────────────────
const MARQUEE_ITEMS = [
  "Frontend Engineer", "React & TypeScript", "Building Chromaly",
  "NYC", "Ex-AmEx", "Accessibility", "UI/UX Enthusiast",
];

const Marquee = () => {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="marquee-section">
      <div className="marquee-track">
        {items.map((item, i) => (
          <span key={i} className="marquee-item">{item}</span>
        ))}
      </div>
    </div>
  );
};

// ── Typewriter hook ───────────────────────────────────────────────────────────
const PHRASES = [
  "a frontend engineer.",
  "building Chromaly.",
  "based in NYC.",
  "a React developer.",
  "passionate about accessibility.",
  "always learning.",
];

function useTypewriter(phrases, typingSpeed = 70, pauseMs = 1800, deleteSpeed = 40) {
  const [displayed, setDisplayed]   = useState("");
  const [phraseIdx, setPhraseIdx]   = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIdx];
    let timeout;

    if (!isDeleting && displayed === current) {
      timeout = setTimeout(() => setIsDeleting(true), pauseMs);
    } else if (isDeleting && displayed === "") {
      setIsDeleting(false);
      setPhraseIdx((i) => (i + 1) % phrases.length);
    } else {
      timeout = setTimeout(() => {
        setDisplayed(isDeleting
          ? current.slice(0, displayed.length - 1)
          : current.slice(0, displayed.length + 1)
        );
      }, isDeleting ? deleteSpeed : typingSpeed);
    }

    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, phraseIdx, phrases, typingSpeed, pauseMs, deleteSpeed]);

  return displayed;
}

// ── Fade-up observer ──────────────────────────────────────────────────────────
function useFadeUp() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── About component ───────────────────────────────────────────────────────────
const About = () => {
  const typewriter = useTypewriter(PHRASES);
  useFadeUp();

  return (
    <>
      <section id="about">
        <div className="about-container">

          {/* Photo with blob */}
          <div className="about-photo-wrap fade-up">
            <div className="about-blob" />
            <img
              src="/_DSC7839.jpg"
              alt="Farha Lina — Software Engineer"
              className="about-image"
              loading="eager"
            />
          </div>

          {/* Text */}
          <div className="about-text">
            <p className="about-eyebrow fade-up">Hi, I'm</p>

            <h1 className="about-name fade-up" style={{ transitionDelay: "0.1s" }}>
              Farha Lina
            </h1>

            <div className="about-typewriter-wrap fade-up" style={{ transitionDelay: "0.2s" }}>
              <span className="about-typewriter-prefix">I'm </span>
              <span className="about-typewriter-text">{typewriter}</span>
              <span className="about-typewriter-cursor" />
            </div>

            <p className="about-description fade-up" style={{ transitionDelay: "0.3s" }}>
             I build clean, scalable web applications with a focus on performance and accessibility. I like taking things that are complicated and making them feel effortless. Outside of coding, you’ll find me exploring languages, café-hopping, and getting lost in books and video games.
            </p>

            <div className="about-skills fade-up" style={{ transitionDelay: "0.4s" }}>
              <p className="skills-label">Core skills</p>
              <div className="skills-grid">
                {["React", "JavaScript", "Node.js", "TypeScript", "Express", "Git"].map((s) => (
                  <span key={s} className="skill-tag">{s}</span>
                ))}
              </div>
            </div>

            <a href="#projects" className="about-cta fade-up" style={{ transitionDelay: "0.5s" }}>
              See my work ↓
            </a>
          </div>

        </div>
      </section>

      <Marquee />
    </>
  );
};

export default About;
